/**
 * The notification list.
 *
 * Two things here are deliberate and would be easy to get wrong.
 *
 * **Reading is an explicit act.** Notifications are not marked read by being
 * scrolled past or by the page being opened. An unread count that empties
 * itself is a count nobody trusts, and the one case where it matters — the
 * notification you meant to come back to — is exactly the one it loses.
 *
 * **The unread count comes from the server, never from counting the rows.**
 * The list is paginated, so the rows on screen are not all the notifications
 * there are, and a count derived from them would be quietly wrong past the
 * first page.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ApiClient } from "@hardauto/core-api";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  Page,
  useToast,
  type BadgeTone,
} from "@hardauto/core-ui";

import { NotificationsApi, type Notification, type NotificationLevel } from "../api";
import { NAMESPACE } from "../i18n";
import "./notifications.css";

const PAGE_SIZE = 30;

const TONE: Record<NotificationLevel, BadgeTone> = {
  info: "info",
  success: "success",
  warning: "warning",
  danger: "danger",
};

export function NotificationsScreen({ client }: { client: ApiClient }) {
  const { t } = useTranslation(NAMESPACE);
  const api = useMemo(() => new NotificationsApi(client), [client]);
  const toast = useToast();

  const [items, setItems] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  const [cursor, setCursor] = useState<string | null>(null);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(
    async (after?: string) => {
      setLoading(true);
      setFailed(false);
      try {
        const page = await api.list({
          unread_only: unreadOnly,
          after_id: after,
          limit: PAGE_SIZE,
        });
        setItems((current) => (after ? [...current, ...page.notifications] : page.notifications));
        setUnread(page.unread);
        setCursor(page.next_cursor);
      } catch {
        setFailed(true);
      } finally {
        setLoading(false);
      }
    },
    [api, unreadOnly],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const markOne = useCallback(
    async (item: Notification) => {
      try {
        const result = await api.markRead(item.id);
        setUnread(result.unread);
        setItems((current) =>
          // Filtered out when the list is showing unread only — leaving a read
          // row in a list labelled "unread" contradicts the filter the user
          // chose. Otherwise it stays, stamped, so the eye does not lose it.
          unreadOnly
            ? current.filter((candidate) => candidate.id !== item.id)
            : current.map((candidate) =>
                candidate.id === item.id
                  ? { ...candidate, read_at: new Date().toISOString() }
                  : candidate,
              ),
        );
      } catch {
        toast.show(t("error.mark"), { tone: "danger" });
      }
    },
    [api, t, toast, unreadOnly],
  );

  const markAll = useCallback(async () => {
    try {
      const result = await api.markAllRead();
      toast.show(t("allRead", { count: result.marked }), { tone: "success" });
      await load();
    } catch {
      toast.show(t("error.mark"), { tone: "danger" });
    }
  }, [api, load, t, toast]);

  const body = () => {
    if (loading && items.length === 0) return <LoadingState label={t("loading")} />;
    if (failed) {
      return (
        <ErrorState
          title={t("error.load")}
          onRetry={() => {
            void load();
          }}
          retryLabel={t("error.retry")}
        />
      );
    }
    if (items.length === 0) {
      return unreadOnly ? (
        <EmptyState title={t("emptyUnread.title")} description={t("emptyUnread.body")} />
      ) : (
        <EmptyState title={t("empty.title")} description={t("empty.body")} />
      );
    }

    return (
      <ul className="hardauto-notifications">
        {items.map((item) => (
          <li key={item.id}>
            <Card>
              <div className="hardauto-notification">
                <div className="hardauto-notification__head">
                  <Badge tone={TONE[item.level]}>{item.kind}</Badge>
                  {item.read_at === null ? <Badge tone="warning">{t("unreadBadge")}</Badge> : null}
                  {item.created_at ? (
                    // A machine-readable timestamp alongside the rendered one,
                    // so "2 September" is unambiguous to anything reading the
                    // page rather than looking at it.
                    <time dateTime={item.created_at}>
                      {new Date(item.created_at).toLocaleString()}
                    </time>
                  ) : null}
                </div>

                <h3 className="hardauto-notification__title">{item.title}</h3>
                <p className="hardauto-notification__body">{item.body}</p>

                {item.read_at === null ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    // Named, because a list of identical "Mark as read"
                    // buttons tells a screen-reader user nothing about which
                    // one they are on.
                    aria-label={t("markReadOne", { title: item.title })}
                    onClick={() => {
                      void markOne(item);
                    }}
                  >
                    {t("markRead")}
                  </Button>
                ) : null}
              </div>
            </Card>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <Page
      title={t("title")}
      description={t("intro")}
      actions={
        <>
          <Button
            variant="ghost"
            aria-pressed={unreadOnly}
            onClick={() => {
              setUnreadOnly((current) => !current);
              setCursor(null);
            }}
          >
            {t(unreadOnly ? "filter.all" : "filter.unread")}
          </Button>
          <Button
            disabled={unread === 0}
            onClick={() => {
              void markAll();
            }}
          >
            {t("markAllRead")}
          </Button>
        </>
      }
    >
      {body()}

      {cursor ? (
        <Button
          variant="ghost"
          disabled={loading}
          onClick={() => {
            void load(cursor);
          }}
        >
          {t("more")}
        </Button>
      ) : null}
    </Page>
  );
}
